import { useGetList, type Identifier } from "ra-core";
import { useMemo } from "react";

import type { Pipeline, PipelineCardSummary, PipelineStage } from "../../types";
import { sortByPosition } from "../positions";
import { useInfiniteRecords } from "../useInfiniteRecords";

const ALL = { page: 1, perPage: 1000 };

export const usePipelines = () => {
  const { data = [], isPending } = useGetList<Pipeline>("pipelines", {
    pagination: ALL,
    sort: { field: "position", order: "ASC" },
  });
  return { pipelines: useMemo(() => sortByPosition(data), [data]), isPending };
};

export const usePipelineStages = (pipelineId: Identifier | undefined) => {
  const { data = [], isPending } = useGetList<PipelineStage>(
    "pipeline_stages",
    {
      pagination: ALL,
      sort: { field: "position", order: "ASC" },
      filter: { pipeline_id: pipelineId },
    },
    { enabled: pipelineId != null },
  );
  return { stages: useMemo(() => sortByPosition(data), [data]), isPending };
};

const POSITION_SORT = { field: "position", order: "ASC" } as const;

/** Cards of one stage matching the board filters, 25 at a time. */
export const useInfiniteStageCards = (filter: Record<string, unknown>) => {
  const { records: cards, ...pagination } =
    useInfiniteRecords<PipelineCardSummary>(
      "pipeline_cards_summary",
      filter,
      POSITION_SORT,
    );
  return { cards, ...pagination };
};

/** Number of cards in a stage, filters ignored; undefined while loading. */
export const useStageCardCount = (stageId: Identifier | undefined) => {
  const { total } = useGetList(
    "pipeline_cards",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
      filter: { stage_id: stageId },
    },
    { enabled: stageId != null },
  );
  return stageId != null ? total : undefined;
};
